import { format } from "date-fns";

const TBC_VALUES = new Set([
  "not specified",
  "flexible / tbc",
  "flexible",
  "tbc",
  "tba",
  "",
]);

export const MOVE_DATE_FILTER_OPTIONS = {
  ALL: "all",
  UPCOMING: "upcoming",
  THIS_WEEK: "this_week",
  THIS_MONTH: "this_month",
  TBC: "tbc",
  PAST: "past",
};

export const MOVE_DATE_FILTER_LABELS = {
  [MOVE_DATE_FILTER_OPTIONS.ALL]: "All dates",
  [MOVE_DATE_FILTER_OPTIONS.UPCOMING]: "Upcoming",
  [MOVE_DATE_FILTER_OPTIONS.THIS_WEEK]: "This week",
  [MOVE_DATE_FILTER_OPTIONS.THIS_MONTH]: "This month",
  [MOVE_DATE_FILTER_OPTIONS.TBC]: "Flexible / TBC",
  [MOVE_DATE_FILTER_OPTIONS.PAST]: "Past",
};

export const QUOTE_SORT_OPTIONS = {
  NEWEST: "newest",
  MOVE_SOON: "move_soon",
  MOVE_LATEST: "move_latest",
};

export const QUOTE_SORT_LABELS = {
  [QUOTE_SORT_OPTIONS.NEWEST]: "Newest first",
  [QUOTE_SORT_OPTIONS.MOVE_SOON]: "Move date soonest",
  [QUOTE_SORT_OPTIONS.MOVE_LATEST]: "Move date latest",
};

const FAR_FUTURE = Number.MAX_SAFE_INTEGER;

function parseMoveDateValue(value) {
  if (!value || typeof value !== "string") {
    return null;
  }

  const normalized = value.trim().toLowerCase();
  if (TBC_VALUES.has(normalized)) {
    return null;
  }

  const parsed = new Date(value.trim());
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed;
}

function startOfDay(date) {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

function endOfDay(date) {
  const next = new Date(date);
  next.setHours(23, 59, 59, 999);
  return next;
}

/**
 * @param {{ dates?: string[], date?: string }} quote
 * @returns {Date|null}
 */
export function getQuoteMoveDate(quote) {
  const candidates = [];

  if (Array.isArray(quote?.dates)) {
    for (const value of quote.dates) {
      const parsed = parseMoveDateValue(value);
      if (parsed) {
        candidates.push(parsed);
      }
    }
  }

  const single = parseMoveDateValue(quote?.date);
  if (single) {
    candidates.push(single);
  }

  if (candidates.length === 0) {
    return null;
  }

  return candidates.reduce((earliest, current) =>
    current < earliest ? current : earliest
  );
}

export function isQuoteMoveDateTbc(quote) {
  return getQuoteMoveDate(quote) === null;
}

/**
 * @param {{ dates?: string[], date?: string }} quote
 * @returns {string}
 */
export function formatQuoteMoveDate(quote) {
  const moveDate = getQuoteMoveDate(quote);
  if (!moveDate) {
    const raw =
      (Array.isArray(quote?.dates) && quote.dates.length > 0 && quote.dates[0]) ||
      quote?.date ||
      null;

    if (!raw) {
      return "Not specified";
    }

    const normalized = String(raw).trim().toLowerCase();
    if (TBC_VALUES.has(normalized)) {
      return "Flexible / TBC";
    }

    return raw;
  }

  return format(moveDate, "MMM dd, yyyy");
}

/**
 * @param {{ dates?: string[], date?: string }} quote
 * @param {string} filter
 * @param {Date} [referenceDate]
 * @returns {boolean}
 */
export function matchesMoveDateFilter(
  quote,
  filter,
  referenceDate = new Date()
) {
  if (!filter || filter === MOVE_DATE_FILTER_OPTIONS.ALL) {
    return true;
  }

  const moveDate = getQuoteMoveDate(quote);
  const todayStart = startOfDay(referenceDate);

  if (filter === MOVE_DATE_FILTER_OPTIONS.TBC) {
    return moveDate === null;
  }

  if (!moveDate) {
    return false;
  }

  const moveDay = startOfDay(moveDate);

  if (filter === MOVE_DATE_FILTER_OPTIONS.PAST) {
    return moveDay < todayStart;
  }

  if (filter === MOVE_DATE_FILTER_OPTIONS.UPCOMING) {
    return moveDay >= todayStart;
  }

  if (filter === MOVE_DATE_FILTER_OPTIONS.THIS_WEEK) {
    const weekEnd = endOfDay(referenceDate);
    weekEnd.setDate(weekEnd.getDate() + 7);
    return moveDay >= todayStart && moveDay <= weekEnd;
  }

  if (filter === MOVE_DATE_FILTER_OPTIONS.THIS_MONTH) {
    const monthStart = new Date(
      referenceDate.getFullYear(),
      referenceDate.getMonth(),
      1
    );
    const monthEnd = endOfDay(
      new Date(referenceDate.getFullYear(), referenceDate.getMonth() + 1, 0)
    );
    return moveDay >= startOfDay(monthStart) && moveDay <= monthEnd;
  }

  return true;
}

export function filterQuotesByMoveDate(quotes, filter, referenceDate = new Date()) {
  return quotes.filter((quote) =>
    matchesMoveDateFilter(quote, filter, referenceDate)
  );
}

function parseCreatedAt(quote) {
  if (!quote?.createdAt) {
    return 0;
  }
  const time = new Date(quote.createdAt).getTime();
  return Number.isNaN(time) ? 0 : time;
}

function parseMoveTime(quote) {
  const moveDate = getQuoteMoveDate(quote);
  return moveDate ? moveDate.getTime() : FAR_FUTURE;
}

function parseMoveTimeLatest(quote) {
  const moveDate = getQuoteMoveDate(quote);
  return moveDate ? moveDate.getTime() : 0;
}

export function sortQuotes(quotes, sort = QUOTE_SORT_OPTIONS.NEWEST) {
  const result = [...quotes];

  result.sort((a, b) => {
    switch (sort) {
      case QUOTE_SORT_OPTIONS.MOVE_SOON:
        return parseMoveTime(a) - parseMoveTime(b);
      case QUOTE_SORT_OPTIONS.MOVE_LATEST:
        return parseMoveTimeLatest(b) - parseMoveTimeLatest(a);
      case QUOTE_SORT_OPTIONS.NEWEST:
      default:
        return parseCreatedAt(b) - parseCreatedAt(a);
    }
  });

  return result;
}

export function applyQuoteListFilters(
  quotes,
  { moveDateFilter = MOVE_DATE_FILTER_OPTIONS.ALL, sort = QUOTE_SORT_OPTIONS.NEWEST } = {},
  referenceDate = new Date()
) {
  const filtered = filterQuotesByMoveDate(quotes, moveDateFilter, referenceDate);
  return sortQuotes(filtered, sort);
}
